import { comparer } from "mobx";
import type { TWorkflowDraftSavePayload, TWorkflowElementKind } from "@/types/form-workflow";

/** What one workflow autosave changed, as the props of `workflow_updated`. */
export interface TWorkflowSaveChanges {
  /** `["steps"]` when any step was added, removed or changed, else empty. The
   *  save body carries the workflow's name too, but the store reads it from the
   *  stored workflow, so a save never renames it: the form's name is the
   *  intake store's `form_updated`. */
  fields: string[];
  steps_added: number;
  steps_removed: number;
  /** Steps kept by id whose saved content differs: settings, name or order. */
  steps_changed: number;
  /** The kinds of the added steps, each once. */
  added_kinds: TWorkflowElementKind[];
}

/**
 * Diff a workflow save body against the last one the server stored (NG-2058).
 * The builder autosaves the whole document, so the save body alone can't say
 * whether a step was added or a condition edited. Both sides go through the
 * same serializer, so a step whose write is equal is one this save left alone.
 * `before` is null when nothing has been stored yet, and then every step counts
 * as added.
 */
export function workflowSaveChanges(
  before: TWorkflowDraftSavePayload | null,
  after: TWorkflowDraftSavePayload,
): TWorkflowSaveChanges {
  const beforeById = new Map((before?.elements ?? []).map((el) => [el.id, el]));
  const afterElements = after.elements ?? [];
  const afterIds = new Set(afterElements.map((el) => el.id));
  const added = afterElements.filter((el) => !beforeById.has(el.id));
  const removed = [...beforeById.keys()].filter((id) => !afterIds.has(id)).length;
  const changed = afterElements.filter((el) => {
    const prev = beforeById.get(el.id);
    return prev !== undefined && !comparer.structural(prev, el);
  }).length;

  return {
    fields: added.length > 0 || removed > 0 || changed > 0 ? ["steps"] : [],
    steps_added: added.length,
    steps_removed: removed,
    steps_changed: changed,
    added_kinds: [...new Set(added.map((el) => el.kind))],
  };
}
